import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import type { UserDeviceDto } from '@fas/types';
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, Field, Input, Spinner, StatusBadge } from '@fas/ui';
import { api, errorMessage } from '@/lib/api';
import { loadDeviceKey } from '@/lib/device';
import { deviceProof } from '@/lib/device-proof';
import { formatDateTime } from '@/lib/format';
import { USER_DEVICE_STATUS } from '@/features/attendance/labels';

/** "Chrome on Android" — a suggested name the owner can change. */
export function suggestedDeviceName(ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent): string {
  const browser = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Android/.test(ua) ? 'Android' : /iPhone/.test(ua) ? 'iPhone' : /iPad/.test(ua) ? 'iPad' : /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : null;
  return os ? `${browser} on ${os}` : browser;
}

/**
 * The devices the user clocks in with (§37 optional device registration, Phase 2B). The
 * attendance policy may require a registered device; Human Resources may need to approve it.
 */
export function DevicesCard() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const devices = useQuery({ queryKey: ['/devices', 'mine'], queryFn: () => api.get<UserDeviceDto[]>('/devices/mine') });
  const thisDevice = useQuery({ queryKey: ['device-key'], queryFn: async () => (await loadDeviceKey())?.keyId ?? null, staleTime: Infinity });
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState(suggestedDeviceName);
  const [removing, setRemoving] = useState<UserDeviceDto | null>(null);

  // Links from the attendance screens open this card (/account#devices).
  useEffect(() => {
    if (location.hash === '#devices') document.getElementById('devices')?.scrollIntoView?.({ block: 'start' });
  }, [location.hash]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['/devices'] });
    await queryClient.invalidateQueries({ queryKey: ['/attendance', 'today'] });
  };
  const register = useMutation({
    mutationFn: async () => {
      const proof = await deviceProof('REGISTER', '');
      if (!proof) throw new Error('This browser cannot keep a device key.');
      return api.post<UserDeviceDto>('/devices', { label: name.trim(), proof });
    },
    onSuccess: async (device) => {
      toast.success(device.status === 'ACTIVE' ? 'Device registered' : 'Device registered — waiting for approval by Human Resources');
      setAdding(false);
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.post<UserDeviceDto>(`/devices/mine/${id}/remove`),
    onSuccess: async () => {
      toast.success('Device removed');
      setRemoving(null);
      await refresh();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const live = devices.data?.filter((d) => d.status !== 'REVOKED') ?? [];
  const removed = devices.data?.filter((d) => d.status === 'REVOKED') ?? [];
  const keyId = thisDevice.data;
  const registeredHere = live.some((d) => d.keyId === keyId);

  return (
    <Card id="devices">
      <CardHeader>
        <CardTitle>Devices</CardTitle>
        <CardDescription>
          The phones and computers you clock in with. Your attendance policy may require a registered device, and Human Resources may need to approve a new one. Each device can be
          registered to one person.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {devices.isLoading || thisDevice.isLoading ? <Spinner /> : null}
        {devices.error ? <Alert tone="danger">{errorMessage(devices.error)}</Alert> : null}
        {keyId === null ? (
          <Alert tone="warning" title="This browser cannot be registered">
            It cannot keep a device key — for example in private browsing, or in an older browser. Use an up-to-date browser in a normal window to register this device.
          </Alert>
        ) : null}

        {live.length > 0 ? (
          <ul className="divide-y divide-slate-100" aria-label="Registered devices">
            {live.map((d) => (
              <li key={d.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <Smartphone className="mt-0.5 size-5 text-slate-500" aria-hidden />
                  <div className="text-sm">
                    <p className="flex flex-wrap items-center gap-1.5 font-medium text-slate-900">
                      {d.label}
                      <StatusBadge label={USER_DEVICE_STATUS[d.status].label} tone={USER_DEVICE_STATUS[d.status].tone} />
                      {d.keyId === keyId ? <Badge tone="info">This device</Badge> : null}
                    </p>
                    <p className="text-slate-600">
                      Registered {formatDateTime(d.registeredAt)}
                      {d.lastUsedAt ? ` · last used ${formatDateTime(d.lastUsedAt)}` : ' · not used yet'}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setRemoving(d)}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        ) : devices.data ? (
          <p className="text-sm text-slate-600">No devices are registered to your account.</p>
        ) : null}

        {keyId && !registeredHere ? (
          adding ? (
            <form
              className="max-w-md space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (name.trim()) register.mutate();
              }}
            >
              {register.error ? <Alert tone="danger">{errorMessage(register.error)}</Alert> : null}
              <Field id="device-name" label="Name of this device" hint="For example “My phone” or “Office laptop”." required>
                {(a11y) => <Input {...a11y} value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />}
              </Field>
              <div className="flex gap-2">
                <Button type="submit" loading={register.isPending} disabled={!name.trim()}>
                  Register
                </Button>
                <Button type="button" variant="outline" onClick={() => setAdding(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <Button variant="outline" onClick={() => setAdding(true)}>
              <Plus aria-hidden /> Register this device
            </Button>
          )
        ) : null}

        {removed.length > 0 ? (
          <details className="text-sm">
            <summary className="cursor-pointer text-slate-700">Removed devices ({removed.length})</summary>
            <ul className="mt-2 space-y-1 text-slate-600">
              {removed.map((d) => (
                <li key={d.id}>
                  {d.label} — removed {d.revokedAt ? formatDateTime(d.revokedAt) : ''}
                  {d.revokeReason ? ` (${d.revokeReason})` : ''}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </CardContent>

      <Dialog open={removing !== null} onOpenChange={(open) => (open ? null : setRemoving(null))}>
        {removing ? (
          <DialogContent title="Remove this device?" description={`“${removing.label}” can no longer be used for attendance. Registering it again may need approval.`} size="sm">
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRemoving(null)}>
                Cancel
              </Button>
              <Button variant="destructive" loading={remove.isPending} onClick={() => remove.mutate(removing.id)}>
                Remove device
              </Button>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </Card>
  );
}
